import { VipPageView, vipPageView } from "@highmarch/app";
import { GrantedItems, OfferDef, OfferItem, VIP_PACKS_PAGE, VipCrest } from "@highmarch/domain";
import { barHtml, fmtCompact } from "../kit";
import { currencyPillsHtml, flagHtml, heroHtml, lockChipHtml, priceButtonHtml, secHtml, sheetHeadHtml, tagHtml, tickTimers, timerChipHtml, wellHtml } from "../components";
import { icon } from "../iconsKit";
import { toast } from "../toast";
import { uiUrl } from "../portraits";
import { confirmPurchase, econCue, flyCurrency, purchasePop } from "../hub/purchaseFx";
import { DealsLink, dealsLink } from "../deals/dealsLink";
import { blockedText, fmtCountdown, itemTiles, needsChoice, priceText, purchaseFailText } from "../deals/dealsModel";
import { artUrl, priceBtnHtml, valueBurstHtml, wellsOf } from "../deals/dealsTiles";
import { bindItemFocus, chooseGearSlot, showOdds } from "../deals/pickSheet";
import { revealThenReceipt } from "../deals/receipt";
import { openShops } from "../shops/shopsScreen";
import {
  BundleState, PackCard, badgeNote, benefitRows, bundleItems, bundleNote, bundleState, clampLevel, crestOf, levelView, packCard, startLevel,
  dailyXpRow, vipDot, xpLine,
} from "./vipModel";
import "../styles/vip.css";
import { SHOP_SKIN } from "../shopSkin";

/**
 * The VIP page (VIP-UI, docs/SHOP.md §8; Kingshot's VIP screen, reference 9, in the game's "Keep & Plate" look): a
 * parchment hub sheet with the standard header; the level card, a navy plate (the crest with the level, the
 * daily VIP XP claim (today's and tomorrow's) and the gold XP bar, the Shop button (→ Shops → VIP) and today's bundle); the browsed level's
 * benefits (arrows and the level strip, 1-12; combat first, values in green, UP on what the level raises), the level's
 * free daily bundle and its one-off Special Pack (Bram's hero). Opened only by a tap in the hub (More → VIP, the Deals VIP chip): never
 * from a level, the end screen or a timer. Free everywhere a save is; the Special Pack shows only with a store.
 * Everything it shows is the app's `vipPageView`; the words are vipModel.ts.
 */

/** How often the countdowns tick. */
const TICK_MS = 1000;
/** A horizontal swipe on the benefits list this long browses a level. */
const SWIPE_PX = 48;
/** How long a bought card's glow plays before the page redraws. */
const REVEAL_MS = 700;

// ------------------------------------------------------------------ pieces shared with the Deals header

/** The crest art (art/interface/icons/vip_crest_<tier>.png); VIP 0 wears the bronze one greyed. */
export function crestUrl(crest: VipCrest): string {
  return uiUrl(`icons/vip_crest_${crest === "none" ? "bronze" : crest}.png`);
}

/** The Deals header's VIP chip (a navy pill like the currency pills): crest, level, a dot while today's VIP XP or bundle waits. */
export function vipChipHtml(level: number, crest: VipCrest, ready: boolean): string {
  return `<button type="button" class="dvipchip${crest === "none" ? " v0" : ""}" aria-label="VIP ${level}${ready ? ", ready to claim" : ""}"><img decoding="async" src="${crestUrl(crest)}" alt=""><b class="disp">VIP ${level}</b>${ready ? `<i class="dot">1</i>` : ""}</button>`;
}

/** The chip's facts from the page view (the Deals header and the More row read the same thing). */
export function vipChipFacts(l: DealsLink): { level: number; crest: VipCrest; dot: number } {
  const v = vipPageView(l.deals, l.now(), l.ctx());
  return { level: v.level, crest: crestOf(v, v.level), dot: vipDot(v) };
}

/** The level badge: the painted crest of the level's tier (bronze greyed at VIP 0) with the level on its foot. */
export function badgeHtml(level: number, crest: VipCrest, extraClass = ""): string {
  return `<span class="vbadge c-${crest} ${extraClass}" role="img" aria-label="VIP ${level}"><img decoding="async" src="${crestUrl(crest)}" alt=""><b class="disp">${level}</b></span>`;
}

// ------------------------------------------------------------------ the page

/** Opens the VIP page, or null outside the hub (never a level or the end screen). */
export function openVip(): HTMLElement | null {
  const l = dealsLink();
  if (!l || !l.hub) return null;
  return new VipPage(l).el;
}

class VipPage {
  readonly el: HTMLDivElement;
  private browsed: number;
  private prices: ReadonlyMap<string, string> = new Map();
  private timer = 0;
  private busy = false;

  constructor(private readonly l: DealsLink) {
    this.el = document.createElement("div");
    this.el.className = `sheet panel deals vippage ${SHOP_SKIN}`;
    this.browsed = startLevel(this.view());
    this.render();
    l.hub!.parent.append(this.el);
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
    void l.prices().then((p) => { this.prices = p; if (this.el.isConnected) this.render(); }).catch(() => undefined);
  }

  private view(): VipPageView { return vipPageView(this.l.deals, this.l.now(), this.l.ctx()); }
  private get parent(): HTMLElement { return this.l.hub?.parent ?? this.el.parentElement ?? document.body; }
  private packDef(level: number): OfferDef | null {
    return this.l.catalog.balance.offers.pages.find((p) => p.id === VIP_PACKS_PAGE)?.offers.find((o) => o.vipLevel === level) ?? null;
  }
  private wells(items: readonly OfferItem[], gearSlots: readonly number[] = [], tag: "button" | "span" = "button"): string {
    return wellsOf(itemTiles(this.l.catalog.balance, items, this.l.deals.level, gearSlots), "sm", tag);
  }

  private render(): void {
    const now = this.l.now(), v = this.view();
    this.browsed = clampLevel(v, this.browsed);
    const scroller = this.el.querySelector<HTMLElement>(".dscroll"), keep = scroller?.scrollTop ?? 0;
    const b = this.browsed, lv = levelView(v, b);
    const def = this.packDef(b), card = packCard(v, b, def);
    const head = sheetHeadHtml({
      title: "VIP", sub: "Perks and a daily bundle",
      portrait: crestUrl(crestOf(v, Math.max(1, v.level))), tint: "navy",
      extra: currencyPillsHtml([{ currency: "gems", amount: fmtCompact(this.l.catalog.economy.wallet.gems) }]),
    });
    this.el.innerHTML = `${head}
      <div class="dscroll"><div class="vpagebody">${this.topHtml(v, now)}${this.benefitsHtml(v)}${this.bundleHtml(v, now)}${card ? this.packHtml(card) : ""}
      <p class="vfoot">Claim VIP XP daily, more on a streak. Every dollar spent pays ${(this.l.catalog.balance.vip.xpPerDollar || 100).toLocaleString("en-US")}. Your rank never falls.</p></div></div>`;
    const ns = this.el.querySelector<HTMLElement>(".dscroll");
    if (ns) ns.scrollTop = keep;
    // the level strip scrolls: keep the browsed level's chip in the middle
    const strip = this.el.querySelector<HTMLElement>(".vlvs"), chip = strip?.querySelector<HTMLElement>(".vlv.on");
    if (strip && chip) strip.scrollLeft = chip.offsetLeft - strip.offsetLeft - (strip.clientWidth - chip.offsetWidth) / 2;
    void lv;
    this.bind(v);
  }

  private topHtml(v: VipPageView, now: number): string {
    const crest = crestOf(v, v.level), ready = v.bundle.ready && v.bundle.items.length > 0 && v.level > 0;
    const mini = v.level === 0 ? lockChipHtml("VIP 1")
      : ready ? tagHtml("best", "CLAIM") : timerChipHtml(fmtCountdown(v.bundle.endsAt - now), { endsAt: v.bundle.endsAt });
    return `<section class="k-card plate vtop">
      <div class="vbadgecol">${badgeHtml(v.level, crest, v.level === 0 ? "v0" : "")}</div>
      <div class="vinfo"><b class="disp vcur">Your level: VIP ${v.level}</b>${this.dailyHtml(v)}
        <div class="vxp">${barHtml(v.fraction, "gold", "vxpbar")}<small><b class="disp vnote${v.nextAt === null ? " max" : ""}">${badgeNote(v)}</b> · ${xpLine(v)}</small></div></div>
      <div class="vside"><button type="button" class="btn blue vshop" aria-label="VIP shop">${icon("shop", 18, "#fff")}<span>Shop</span></button>
        <button type="button" class="vmini${ready ? " ready" : ""}" aria-label="Today's VIP bundle"><img decoding="async" src="${artUrl("hq:icons/chest_legendary.png")}" alt=""><span class="vmlbl">Today's bundle</span>${mini}</button></div>
    </section>`;
  }

  /** Today's VIP XP: Claim once a UTC day (owner 2026-10-24), and what tomorrow's pays. */
  private dailyHtml(v: VipPageView): string {
    const d = dailyXpRow(v);
    const act = d.ready ? `<button type="button" class="btn gold vxclaim disp">CLAIM</button>` : `<span class="k-stamp vxdone">Claimed</span>`;
    return `<div class="vdaily${d.ready ? " ready" : ""}"><span class="vdcol"><small>${d.label}</small><b class="disp">${d.today} <i>VIP XP</i></b></span>
      <span class="vdcol tmr"><small>Tomorrow</small><b class="disp">${d.tomorrow}</b></span>${act}</div>`;
  }

  private benefitsHtml(v: VipPageView): string {
    const b = this.browsed, lv = levelView(v, b);
    if (!lv) return "";
    const rows = benefitRows(lv).map((r) => `<li class="${r.combat ? "c" : r.percent ? "p" : ""}" data-detail="${r.detail}"><span class="nm">${r.label}${r.isNew ? tagHtml("up") : ""}</span><b class="val">${r.value}</b></li>`).join("");
    const strip = v.levels.map((x) => `<button type="button" class="vlv${x.level === b ? " on" : ""}${x.level <= v.level ? " got" : ""}" data-level="${x.level}" aria-label="VIP ${x.level}"${x.level === b ? ` aria-current="true"` : ""}><span>${x.level}</span></button>`).join("");
    const gap = b > v.level ? `${(lv.xp - v.xp).toLocaleString("en-US")} VIP XP to VIP ${b}` : b === v.level ? "Your level" : "Reached";
    const arrow = (dir: "l" | "r", to: number, off: boolean) => `<button type="button" class="icbtn varr ${dir}" aria-label="VIP ${to}"${off ? " disabled" : ""}>${icon("chev", 22, "currentColor", 3)}</button>`;
    return `<section class="k-card vben">
      <div class="vbhead">${arrow("l", b - 1, b <= 1)}${secHtml(`VIP ${b} Benefits`)}${arrow("r", b + 1, b >= v.levels.length)}</div>
      <div class="vlvs" role="group" aria-label="VIP levels">${strip}</div><p class="vgap${b === v.level ? " cur" : b < v.level ? " had" : ""}">${b > v.level ? icon("lock", 13) : ""}${gap}</p>
      <ul class="vrows">${rows}</ul>
    </section>`;
  }

  private bundleHtml(v: VipPageView, now: number): string {
    const b = this.browsed, st: BundleState = bundleState(v, b), items = bundleItems(v, b);
    if (st === "none") return "";
    const right = st === "claim" ? `<button type="button" class="btn gold vclaim disp">CLAIM</button>`
      : st === "claimed" ? timerChipHtml(fmtCountdown(v.bundle.endsAt - now), { endsAt: v.bundle.endsAt })
      : st === "locked" ? lockChipHtml(`VIP ${b}`) : "";
    return `<section class="k-card vbundle s-${st}">${flagHtml(`VIP ${b} Daily Free Bundle`, st === "claim" ? "gold" : "navy")}
      <div class="vbrow">${wellHtml({ art: artUrl("hq:icons/chest_legendary.png"), rarity: 4, size: "sm", tag: "span", className: "vbchest", dim: st === "locked" || st === "below" })}
      <span class="vbtiles">${this.wells(items)}</span><div class="vbright">${right}</div></div><small class="vbnote">${bundleNote(st, b)}</small></section>`;
  }

  private packHtml(c: PackCard): string {
    const def = this.packDef(c.level)!;
    const value = c.pack ? { percent: c.pack.valuePercent, show: c.pack.showValue } : this.l.deals.value(def.items, def.price.cents);
    const vipXp = c.pack?.vipPoints ?? this.l.deals.vipPointsFor(def.price.cents);
    const random = c.pack?.random ?? def.items.some((i) => i.kind === "chest" || i.kind === "luckyChest" || (i.kind === "gear" && i.slot === "any"));
    const odds = "";   // no ODDS button (owner 2026-10-21): tap the chest
    const pay = c.state === "onSale" && c.pack
      ? priceBtnHtml({ offerId: c.offerId, label: priceText(c.pack.price, this.prices), vipPoints: vipXp, blocked: c.pack.blocked && !needsChoice(c.pack.blocked) ? blockedText(c.pack.blocked) : "", choice: needsChoice(c.pack.blocked) }, "main")
      : c.state === "purchased" ? `<span class="k-stamp vpdone">Bought</span>`
      : c.state === "locked" && def ? `<span class="vplock">${lockChipHtml(`VIP ${c.level}`)}${priceButtonHtml({ currency: "real", amount: def.price.label, disabled: true, className: "vpprice" })}</span>` : "";
    const hero = heroHtml({ art: uiUrl("deals/vip_special_pack.webp"), title: c.title, kicker: c.name, blurb: c.note, focus: "70% 52%", className: "vphero" });
    return `<section class="k-card hl vpack s-${c.state}" data-offer="${c.offerId}">${hero}
      ${value.show && c.state !== "purchased" ? valueBurstHtml(value.percent) : ""}
      <div class="vptiles">${this.wells(c.items, c.pack?.gearSlots ?? [])}</div>
      <div class="vpfoot">${odds}${pay}</div></section>`;
  }

  // ------------------------------------------------------------------ behaviour

  private bind(v: VipPageView): void {
    const q = <T extends HTMLElement>(s: string) => [...this.el.querySelectorAll<T>(s)];
    this.el.querySelector<HTMLButtonElement>(".k-head .close")!.onclick = () => this.close();
    const go = (level: number) => { const n = clampLevel(v, level); if (n !== this.browsed) { this.browsed = n; econCue("tap"); this.render(); } };
    this.el.querySelector<HTMLButtonElement>(".varr.l")?.addEventListener("click", () => go(this.browsed - 1));
    this.el.querySelector<HTMLButtonElement>(".varr.r")?.addEventListener("click", () => go(this.browsed + 1));
    for (const b of q<HTMLButtonElement>(".vlv")) b.onclick = () => go(Number(b.dataset.level));
    const list = this.el.querySelector<HTMLElement>(".vben");
    if (list) {
      let x0 = 0, y0 = 0;
      list.addEventListener("touchstart", (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      list.addEventListener("touchend", (e) => {
        const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy) * 1.5) go(this.browsed + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }
    for (const r of q<HTMLElement>(".vrows li")) r.onclick = () => toast(this.parent, r.dataset.detail ?? "");
    this.el.querySelector<HTMLButtonElement>(".vshop")!.onclick = () => this.shop();
    this.el.querySelector<HTMLButtonElement>(".vmini")!.onclick = () => {
      if (v.level > 0 && this.browsed !== v.level) { this.browsed = v.level; this.render(); }
      const card = this.el.querySelector<HTMLElement>(".vbundle");
      card?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      if (v.level === 0) toast(this.parent, "A free bundle every day from VIP 1");
      else purchasePop(card ?? null);
    };
    this.el.querySelector<HTMLButtonElement>(".vclaim")?.addEventListener("click", () => this.claim());
    this.el.querySelector<HTMLButtonElement>(".vxclaim")?.addEventListener("click", () => this.claimXp());
    for (const b of q<HTMLButtonElement>(".vpack .dbuy[data-offer]")) b.onclick = () => void this.buy(b.dataset.offer!, b);
    for (const t of q<HTMLButtonElement>("button.dwell")) t.onclick = (e) => { e.stopPropagation(); toast(this.parent, `${t.dataset.name ?? ""}: ${t.dataset.detail ?? ""}`); };
    bindItemFocus(this.el, this.parent, this.l.catalog.balance, this.l.deals.level);   // a tap: the item in focus, with its odds
  }

  close(): void { window.clearInterval(this.timer); this.el.remove(); }

  private tick(): void {
    if (!this.el.isConnected) { window.clearInterval(this.timer); return; }
    if (tickTimers(this.el, this.l.now(), fmtCountdown) && !this.busy) this.render();
  }

  private shop(): void {
    econCue("tap");
    if (!openShops("vip")) toast(this.parent, "The VIP shop opens soon");
  }

  private claimXp(): void {
    const r = this.l.deals.claimVipXp(this.l.now());
    if (typeof r === "string") { toast(this.parent, r === "not ready" ? "Already claimed today. Back tomorrow" : blockedText(r)); this.render(); return; }
    econCue("buy");
    this.l.changed();
    purchasePop(this.el.querySelector(".vdaily"));
    toast(this.parent, `+${r.toLocaleString("en-US")} VIP XP`);
    this.render();
  }

  private claim(): void {
    const r = this.l.deals.claimVipBundle(this.l.now());
    if (typeof r === "string") { toast(this.parent, r === "not ready" ? "Already claimed today. Back tomorrow" : blockedText(r)); this.render(); return; }
    const card = this.el.querySelector(".vbundle");
    econCue("buy");
    const before = this.l.catalog.economy.wallet.gems - r.gems;
    this.l.changed();
    purchasePop(card);
    if (r.gems > 0) flyCurrency(card, "gems", before, this.l.catalog.economy.wallet.gems);
    revealThenReceipt(this.parent, "VIP DAILY BUNDLE", r, []);
    window.setTimeout(() => { if (this.el.isConnected) this.render(); }, REVEAL_MS);
  }

  private async odds(offerId: string): Promise<void> {
    const v = this.l.deals.view(offerId, this.l.now(), this.l.ctx());
    const def = this.packDef(Number(offerId.split(".").pop()));
    const name = v?.name ?? def?.name ?? "Special Pack";
    await showOdds(this.parent, this.l.catalog.balance, this.l.deals.level, name, v ? [...v.items, ...v.choices, ...v.prizes] : def?.items ?? [], {});
  }

  private async buy(offerId: string, btn: HTMLButtonElement): Promise<void> {
    if (this.busy) return;
    const v = this.l.deals.view(offerId, this.l.now(), this.l.ctx());
    if (!v) { toast(this.parent, "Not on sale at your VIP level"); this.render(); return; }
    if (v.blocked) {
      if (needsChoice(v.blocked)) {
        // A Special Pack's only choice is a picked gear item's slot (VIP 8, 11, 12).
        const rarity = v.items.find((i): i is Extract<OfferItem, { kind: "gear" }> => i.kind === "gear" && i.slot === "pick")?.rarity ?? 0;
        const s = await chooseGearSlot(this.parent, this.l.catalog.balance, rarity, -1);
        if (s !== null) {
          const r = this.l.deals.setGearSlot(offerId, v.gearSlots.length, s);
          if (r !== true) toast(this.parent, blockedText(r)); else this.l.changed();
        }
        this.render();
        return;
      }
      toast(this.parent, blockedText(v.blocked));
      return;
    }
    if (this.l.storeKind === "sandbox") {
      const ok = await confirmPurchase(this.parent, { shop: true,
        title: "TEST PURCHASE", currency: null, price: priceText(v.price, this.prices), confirm: "BUY", className: "dconfirm",
        body: `<p class="dcname"><b>${v.name}</b></p><div class="dctiles">${this.wells(v.items, v.gearSlots, "span")}</div><p>Test build: nothing is charged.</p>`,
      });
      if (!ok) return;
    }
    this.busy = true;
    btn.classList.add("busy");
    btn.disabled = true;
    const card = btn.closest<HTMLElement>(".vpack");
    try {
      const r = await this.l.buy(offerId);
      if (r.status === "granted") {
        econCue("buy");
        const before = this.l.catalog.economy.wallet.gems - r.granted.gems;
        this.l.changed();
        purchasePop(card);
        if (r.granted.gems > 0) flyCurrency(card, "gems", before, this.l.catalog.economy.wallet.gems);
        revealThenReceipt(this.parent, v.name.toUpperCase(), r.granted, r.bankedGear);
        window.setTimeout(() => { if (this.el.isConnected) this.render(); }, REVEAL_MS);
      } else if (r.status === "pending") toast(this.parent, "Awaiting approval: it arrives the moment it's given");
      else if (r.status === "refused" || r.status === "failed") toast(this.parent, purchaseFailText(r.reason));
    } finally {
      this.busy = false;
      if (this.el.isConnected) this.render();
    }
  }
}
